import { useApi } from "@/lib/useApi";
import { api, apiErr } from "@/lib/api";
import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PageHeader, EmptyState } from "@/components/common";
import { fDate, fDateTime, LEAVE_STATUS } from "@/lib/format";
import { HeartPulse, ShieldCheck, Check, X, Info } from "lucide-react";
import { toast } from "sonner";

export default function Absence() {
  const { data: sick, reload: rS } = useApi("/sick-reports");
  const { data: leave, reload: rL } = useApi("/leave-requests");
  const [impact, setImpact] = useState({});

  const ack = async (id) => { try { await api.post(`/sick-reports/${id}/acknowledge`); rS(); toast.success("Sjukanmälan hanterad"); } catch (e) { toast.error(apiErr(e)); } };
  const decide = async (id, decision) => { try { await api.patch(`/leave-requests/${id}/decision`, { decision }); rL(); toast.success(`Ledighet ${decision}`); } catch (e) { toast.error(apiErr(e)); } };
  const loadImpact = async (id) => { try { const r = await api.get(`/leave-requests/${id}/impact`); setImpact((m) => ({ ...m, [id]: r.data })); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <div>
      <PageHeader title="Sjuk & ledighet" icon={HeartPulse}
        subtitle="Sjukanmälan är ett operativt bemanningsflöde – ingen diagnos, ingen sjukfrånvarostatistik. Kollegor får inte veta vem som är sjuk." />
      <Tabs defaultValue="sick">
        <TabsList>
          <TabsTrigger value="sick" data-testid="tab-sick">Sjukanmälningar {(sick || []).filter((s) => s.status === "mottagen").length > 0 && <Badge variant="destructive" className="ml-1">{(sick || []).filter((s) => s.status === "mottagen").length}</Badge>}</TabsTrigger>
          <TabsTrigger value="leave" data-testid="tab-leave">Ledighetsansökningar</TabsTrigger>
        </TabsList>

        <TabsContent value="sick" className="mt-4">
          {(sick || []).length === 0 ? <EmptyState icon={ShieldCheck} title="Inga sjukanmälningar" text="Bemanningskontroll sker automatiskt när en anmälan tas emot." /> : (
            <div className="space-y-2">
              {sick.map((s) => (
                <Card key={s.id} className="flex flex-wrap items-center gap-3 p-4" data-testid={`sick-${s.id}`}>
                  <HeartPulse className="h-5 w-5 text-red-500" />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium">{s.employee_name}</div>
                    <div className="text-xs text-muted-foreground">Från {fDateTime(s.start_time)}{s.expected_return && ` · förväntad återgång ${fDate(s.expected_return)}`}</div>
                    <div className="mt-1 text-xs text-muted-foreground">Registrera frånvaron i ordinarie HR-system. Bemanningskontroll genomförd.</div>
                  </div>
                  <Badge variant={s.status === "mottagen" ? "destructive" : "secondary"}>{s.status}</Badge>
                  {s.status === "mottagen" && <Button size="sm" onClick={() => ack(s.id)} data-testid={`ack-${s.id}`}><Check className="mr-1 h-4 w-4" />Markera hanterad</Button>}
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="leave" className="mt-4">
          {(leave || []).length === 0 ? <EmptyState icon={Info} title="Inga ansökningar" text="Ledighetsansökningar från medarbetare visas här." /> : (
            <div className="space-y-2">
              {leave.map((l) => (
                <Card key={l.id} className="p-4" data-testid={`leave-${l.id}`}>
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium">{l.employee_name} · {l.type}</div>
                      <div className="text-xs text-muted-foreground">{fDate(l.start_date)} – {fDate(l.end_date)}{l.comment && ` · ${l.comment}`}</div>
                    </div>
                    <Badge variant={l.status === "godkänd" ? "default" : l.status === "avslagen" ? "destructive" : "secondary"}>{LEAVE_STATUS[l.status] || l.status}</Badge>
                    <Button size="sm" variant="outline" onClick={() => loadImpact(l.id)} data-testid={`impact-${l.id}`}>Analysera påverkan</Button>
                    {["inskickad", "utkast"].includes(l.status) && <>
                      <Button size="sm" onClick={() => decide(l.id, "godkänd")} data-testid={`approve-leave-${l.id}`}><Check className="mr-1 h-4 w-4" />Godkänn</Button>
                      <Button size="sm" variant="destructive" onClick={() => decide(l.id, "avslagen")} data-testid={`reject-leave-${l.id}`}><X className="mr-1 h-4 w-4" />Avslå</Button>
                    </>}
                  </div>
                  {impact[l.id] && (
                    <div className="mt-3 rounded-lg bg-secondary/40 p-3 text-xs">
                      Påverkade pass: <span className="font-semibold">{impact[l.id].affected_shifts.length}</span> ·
                      Kompetenser som försvinner: <span className="font-semibold">{impact[l.id].competences_lost.length}</span> ·
                      Rotationsrisk: <span className="font-semibold">{impact[l.id].creates_rotation_risk ? "Ja" : "Nej"}</span>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
